<template>
    <div :class="['relative flex items-center gap-1.5', className]">
        <!-- Inline Star Balance (visible without opening menu) -->
        <button
            v-if="isUserLoggedIn && starBalance !== undefined"
            @click="handleBalanceClick"
            :class="[
                'group/bal relative hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg cursor-pointer min-w-[70px] min-h-[40px]',
                'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20',
                'transition-all active:scale-95',
                requiresAgeVerification ? 'z-[49]' : 'z-[52]'
            ]"
        >
            <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" :class="isCompact ? 'w-3.5 h-3.5' : 'w-4 h-4'" />
            <span :class="['font-bold text-white/90 tabular-nums', isCompact ? 'text-[11px]' : 'text-xs']">{{ starBalance.toLocaleString() }}</span>
            <!-- Tooltip -->
            <span class="pointer-events-none absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-white px-2.5 py-1 text-[11px] font-semibold text-zinc-900 opacity-0 shadow-lg transition-opacity group-hover/bal:opacity-100">
                Your Star Balance
            </span>
        </button>

        <!-- Trigger Button - z-[52] so it sits above backdrop (z-[51]) and is not blurred -->
        <button
            ref="buttonRef"
            @click="isOpen = !isOpen"
            :class="[
                'relative',
                isCompact ? 'w-8 h-8' : 'w-10 h-10',
                'rounded-lg',
                'flex items-center justify-center',
                'transition-all',
                isOpen ? 'bg-white/15 text-white' : 'bg-white/5 hover:bg-white/10 text-white/70 hover:text-white',
                'border border-white/10 hover:border-white/20',
                'active:scale-95',
                requiresAgeVerification ? 'z-[49]' : 'z-[52]'
            ]"
            aria-label="Menu"
            :aria-expanded="isOpen"
        >
            <MoreVertical :class="isCompact ? 'w-4 h-4' : 'w-5 h-5'" />
        </button>

        <!-- Flyout Menu -->
        <template v-if="isOpen">
            <Teleport to="body">
            <!-- Backdrop - dim and blur content behind (works on FeedV2, TagPageV2, etc.) -->
            <div
                class="fixed inset-0 z-[51] bg-black/20 backdrop-blur-md"
                @click="isOpen = false"
            />

            <!-- Menu Panel - HIGHEST z-index to appear above everything -->
            <div
                ref="menuRef"
                class="fixed z-[52] w-64 bg-zinc-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
                :style="menuPanelStyle"
            >
                <!-- Logged-In Member Menu -->
                <template v-if="isUserLoggedIn">
                    <!-- Primary Actions -->
                    <div class="p-2">
                        <KebabMenuItem
                            :icon="Star"
                            label="My Sponsorships"
                            :type="'Link'"
                            :href="getMineAuditionsUrl(isUserLoggedIn, routePrefix)"
                            variant="primary"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            :icon="Sparkles"
                            label="Get More Stars"
                            @click="handleGetMoreStarsClick"
                        >
                            <template #badge>
                                <span class="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/10">
                                    <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-3.5 h-3.5" />
                                    <span class="text-white/90 text-[11px] font-bold">{{ starBalance }}</span>
                                </span>
                            </template>
                        </KebabMenuItem>
                    </div>

                    <KebabMenuDivider />

                    <!-- Secondary Actions -->
                    <div class="p-2">
                        <KebabMenuItem
                            :type="'Link'"
                            :icon="Plus"
                            label="Create"
                            :href="getCreateCastingUrl(isUserLoggedIn, routePrefix)"
                            @click="handleCreateCastingClick"
                        >
                            <template #badge>
                                <span class="px-1.5 py-0.5 rounded-md bg-blue-600/20 border border-blue-500/30 text-blue-300 text-[10px] font-bold uppercase tracking-wide">New</span>
                            </template>
                        </KebabMenuItem>
                        <KebabMenuItem
                            v-if="!requiresAgeVerification"
                            :type="'a'"
                            :icon="Video"
                            label="Pitches"
                            :href="isUserLoggedIn ? route('members2.producer') : route('www.producer')"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            v-if="!requiresAgeVerification"
                            :type="'a'"
                            :icon="Video"
                            label="Auditions"
                            :href="isUserLoggedIn ? route('members2.reels.tag') : route('www.reels.tag')"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            v-if="!requiresAgeVerification"
                            :type="'a'"
                            :icon="Video"
                            label="Browse Library"
                            :href="isUserLoggedIn ? route('members2.main.home') : route('www.main.home')"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            v-if="!requiresAgeVerification"
                            :type="'a'"
                            :icon="Info"
                            label="How Auditions Work"
                            :href="isUserLoggedIn ? route('members2.auditions.lander') : route('www.auditions.lander')"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            v-if="!requiresAgeVerification"
                            :type="'a'"
                            :icon="Info"
                            label="Submit Your Audition"
                            :href="isUserLoggedIn ? route('members2.auditions.lander') + '#for-models' : route('www.auditions.lander') + '#for-models'"
                            @click="isOpen = false"
                        />
                    </div>

                    <KebabMenuDivider />

                    <!-- Account Actions -->
                    <div class="p-2">
                        <KebabMenuItem
                            :type="'Link'"
                            :icon="User"
                            label="My Sponsor Page"
                            :href="route('members2.profile.my', { slug: sharedProps?.auth?.user?.display_name || `Naughty${sharedProps?.auth?.user?.id}` })"
                            @click="trackFathomEvent(getFathomPagePrefix(routePrefix) + '_KebabMenu_My_Sponsor_Page_Clicked'); isOpen = false"
                        />
                        <KebabMenuItem
                            :type="'Link'"
                            :icon="Clapperboard"
                            label="My Pitches"
                            :href="route('members2.profile.my', { slug: sharedProps?.auth?.user?.display_name || `Naughty${sharedProps?.auth?.user?.id}` })"
                            @click="trackFathomEvent(getFathomPagePrefix(routePrefix) + '_KebabMenu_My_Pitches_Clicked'); isOpen = false"
                        />
                        <KebabMenuItem
                            :type="'a'"
                            :icon="Settings"
                            label="Account Settings"
                            :href="route('members2.account.profile')"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            :type="'a'"
                            :icon="HelpCircle"
                            label="Help & Support"
                            href="https://support.latouraineinc.com"
                            @click="isOpen = false"
                        />
                    </div>

                    <KebabMenuDivider />

                    <!-- Sign Out -->
                    <div class="p-2">
                        <KebabMenuItem
                            :type="'a'"
                            :icon="LogOut"
                            label="Sign Out"
                            :href="route('members2.auth.logout')"
                            variant="danger"
                            @click="isOpen = false"
                        />
                    </div>
                </template>

                <!-- Guest Menu -->
                <template v-else>
                    <!-- Primary CTA -->
                    <div class="p-2">
                        <KebabMenuItem
                            :icon="LogIn"
                            label="Join Free"
                            @click="handleGuestGetStarsClick"
                            variant="primary"
                        />
                        <KebabMenuItem
                            :type="'a'"
                            :icon="User"
                            label="Sign In"
                            :href="route('members2.auth.login', { dest: currentUrl })"
                        />
                    </div>

                    <KebabMenuDivider />

                    <!-- Info Actions -->
                    <div class="p-2">
                        <KebabMenuItem
                            :type="'Link'"
                            :icon="Plus"
                            :href="getCreateCastingUrl(isUserLoggedIn, routePrefix)"
                            label="Create"
                            @click="handleCreateCastingClick"
                        >
                            <template #badge>
                                <span class="px-1.5 py-0.5 rounded-md bg-blue-600/20 border border-blue-500/30 text-blue-300 text-[10px] font-bold uppercase tracking-wide">New</span>
                            </template>
                        </KebabMenuItem>
                        <KebabMenuItem
                            v-if="!requiresAgeVerification"
                            :type="'a'"
                            :icon="Video"
                            label="Pitches"
                            :href="isUserLoggedIn ? route('members2.producer') : route('www.producer')"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            v-if="!requiresAgeVerification"
                            :type="'a'"
                            :icon="Video"
                            label="Auditions"
                            :href="isUserLoggedIn ? route('members2.reels.tag') : route('www.reels.tag')"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            v-if="!requiresAgeVerification"
                            :type="'a'"
                            :icon="Video"
                            label="Browse Library"
                            :href="isUserLoggedIn ? route('members2.main.home') : route('www.main.home')"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            :type="'a'"
                            :icon="Info"
                            label="How Auditions Work"
                            :href="isUserLoggedIn ? route('members2.auditions.lander') : route('www.auditions.lander')"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            :type="'a'"
                            :icon="Info"
                            label="Submit Your Audition"
                            :href="isUserLoggedIn ? route('members2.auditions.lander') + '#for-models' : route('www.auditions.lander') + '#for-models'"
                            @click="isOpen = false"
                        />
                        <KebabMenuItem
                            :type="'a'"
                            :icon="HelpCircle"
                            label="Help & Support"
                            href="https://support.latouraineinc.com"
                            @click="isOpen = false"
                        />
                    </div>
                </template>
            </div>
            </Teleport>
        </template>
    </div>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue'
import {
    MoreVertical,
    LogIn,
    Video,
    HelpCircle,
    Info,
    Star,
    Settings,
    LogOut,
    Sparkles,
    User,
    Clapperboard,
    Plus
} from 'lucide-vue-next'
import KebabMenuItem from "@/Components/Reels/KebabMenuItem.vue";
import KebabMenuDivider from "@/Components/Reels/KebabMenuDivider.vue";
import {getCreateCastingUrl, getMineAuditionsUrl} from "@/Helpers/auditions";
import {usePage} from "@inertiajs/vue3";
import {trackFathomEvent, getFathomPostPrefix, getFathomPagePrefix} from "@/Helpers/analytics";

const props = defineProps({
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    isGoldUser: {
        type: Boolean,
        default: false
    },
    variant: {
        type: String,
        default: 'default',
        validator: (value) => ['default', 'compact'].includes(value)
    },
    className: {
        type: String,
        default: ''
    },
    routePrefix: {
        type: String
    },
    starBalance: {
        type: Number,
        default: 0
    },
    requiresAgeVerification: {
        type: Boolean,
        default: false
    }
})

const { props: sharedProps } = usePage()

const emit = defineEmits(['joinClick', 'update:open', 'goldJoinClick'])

const fathomPrefix = computed(() => getFathomPostPrefix(props.routePrefix))

const handleBalanceClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Balance_Badge_Clicked`)
    emit('goldJoinClick')
}

const handleGetMoreStarsClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Menu_Get_More_Stars_Clicked`)
    isOpen.value = false
    emit('goldJoinClick')
}

const handleGuestGetStarsClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Menu_Get_Stars_Clicked`)
    isOpen.value = false
    emit('joinClick')
}

const handleCreateCastingClick = () => {
    trackFathomEvent(`${fathomPrefix.value}_Menu_Create_Casting_Clicked`)
    isOpen.value = false
}

const isOpen = ref(false)
const menuRef = ref(null)
const buttonRef = ref(null)

const isCompact = computed(() => props.variant === 'compact')

const menuPanelStyle = computed(() => {
    if (!buttonRef.value) return {}
    const rect = buttonRef.value.getBoundingClientRect()
    return {
        top: `${rect.bottom + 8}px`,
        right: `${window.innerWidth - rect.right}px`,
    }
})

const currentUrl = computed(() => {
    if (typeof window !== 'undefined' && window.location) {
        return window.location.pathname + window.location.search
    }
    return ''
})

// Close menu when clicking outside
const handleClickOutside = (event) => {
    if (
        menuRef.value &&
        !menuRef.value.contains(event.target) &&
        buttonRef.value &&
        !buttonRef.value.contains(event.target)
    ) {
        isOpen.value = false
    }
}

// Close on escape key
const handleEscape = (event) => {
    if (event.key === 'Escape') {
        isOpen.value = false
    }
}

// Watch isOpen to add/remove event listeners and notify parent (for content blur)
watch(isOpen, (newValue) => {
    emit('update:open', newValue)
    if (newValue) {
        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscape)
    } else {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscape)
    }
})

// Cleanup on unmount
onUnmounted(() => {
    document.removeEventListener('mousedown', handleClickOutside)
    document.removeEventListener('keydown', handleEscape)
})
</script>
